/**
 * Especial → SESSP → Dose Certa — CRUD no padrão Inventários V2.
 * Grade + Filtrar + clique abre modal de detalhe.
 */
import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter from '../../versa/filter.js';
import router from '../../versa/router.js';
import { notify, ask } from '../../versa/msgbox.js';
import view from '../views/especial-dose-certa.js';
import form, { openHelp } from '../forms/especial-dose-certa.js';
import { labelEsteira, esteiraFilterTokens } from '../utils/dose-certa-labels.js';
import { openOutputsFiltered } from '../utils/navigate-to-outputs.js';
import { openWavesFiltered } from '../utils/navigate-to-waves.js';
import { fetchEspecialAcl, hasEspecialTenant } from '../utils/especial-acl.js';
import { formatBiHttpError } from '../views/bi-http-error.js';
import * as types from '../types.js';

const especialDoseCerta = async function ()
{
	const work = document.getElementById('workarea');
	if (!work) return;

	const acl = await fetchEspecialAcl();
	if (!hasEspecialTenant('sessp', acl)) {
		work.innerHTML = `
			<div class="title">Dose Certa</div>
			<p class="mts" style="max-width:40rem;">
				Seu usuário não tem permissão para o módulo <strong>Especial → SESSP</strong>.
				Peça ao administrador o endpoint <strong>GET /especial/sessp</strong>
				(grupo <strong>ESPECIAL SESSP</strong>).
			</p>
		`;
		return;
	}

	const dc_crud = crud.create({

		_lastRows: null,
		_lastFilterKey: null,

		async loadBaseData() {
			try {
				const data = await api.call(api.list_all_companies, this.queryAll());
				await db.set('companies', data);
			} catch (err) {
				notify(err);
			}
		},

		insertRecord() {
			/* Pacotes vêm do TechHub — sem inserção manual */
		},

		async editFilter() {
			filter.edit(this.applyFilter.bind(this), this.filter);
		},

		async applyFilter(data) {
			this.filter = data;
			this.filterExplicitlyApplied = true;
			this._invalidateRowsCache();
			const companyId = this._resolveCompanyIdFromFilter();
			if (companyId)
				this._persistCompanyFilter(companyId);
			await this.loadPage(1);
		},

		_invalidateRowsCache() {
			this._lastRows = null;
			this._lastFilterKey = null;
		},

		reloadPage() {
			this._invalidateRowsCache();
			this.loadPage(this.page_number > 0 ? this.page_number : 1);
		},

		_persistCompanyFilter(companyId) {
			try {
				localStorage.setItem('especial-dose-certa.company_id', String(companyId));
			} catch (_) { /* ignore */ }
		},

		_setCompanyFilter(companyId) {
			if (!companyId) return;
			this.filter = {
				...(this.filter || {}),
				'field-0': 'company_id',
				'operator-0': 'eq',
				'value-0': String(companyId),
			};
			this.filterExplicitlyApplied = true;
			this._persistCompanyFilter(companyId);
			this._invalidateRowsCache();
		},

		_resolveCompanyIdFromFilter() {
			for (let i = 0; i < 10; i++) {
				if (this.filter?.[`field-${i}`] === 'company_id') {
					const v = this.filter?.[`value-${i}`];
					return v && String(v).trim() ? String(v).trim() : null;
				}
			}
			return null;
		},

		_resolveStatusFromFilter() {
			for (let i = 0; i < 10; i++) {
				if (this.filter?.[`field-${i}`] === 'status') {
					const v = this.filter?.[`value-${i}`];
					return v && String(v).trim() ? String(v).trim() : null;
				}
			}
			return null;
		},

		_applyClientFilter(rows) {
			const f = this.filter || {};
			const clauses = [];
			for (let i = 0; i < 12; i++) {
				const field = f[`field-${i}`];
				const value = f[`value-${i}`];
				if (!field || value == null || String(value).trim() === '')
					continue;
				if (field === 'company_id')
					continue; // já filtrado na API
				if (field === 'status')
					continue; // já filtrado na API quando informado
				clauses.push({
					field,
					op: String(f[`operator-${i}`] || 'eq').toLowerCase(),
					value: String(value).trim(),
				});
			}
			if (!clauses.length)
				return rows;

			return rows.filter((row) => clauses.every(({ field, op, value }) => {
				let raw = row[field];
				if (field === 'programa')
					raw = [row.programa, row.programa_nome].filter(Boolean).join(' ');
				if (field === 'sorter_status' || field === 'esteira') {
					const label = labelEsteira(row).toLowerCase();
					const code = String(row.sorter_status || '').toLowerCase();
					const tokens = esteiraFilterTokens(value);
					const hay = `${label} ${code} ${row.exception_manual ? 'coletor' : ''}`.toLowerCase();
					const hit = tokens.some((t) => hay.includes(t));
					if (op === 'ne') return !hit;
					return hit;
				}
				if (field === 'output_id') {
					const n = Number(raw);
					const v = Number(value);
					if (op === 'ne') return n !== v;
					if (op === 'ge') return n >= v;
					if (op === 'le') return n <= v;
					return n === v;
				}
				const left = String(raw ?? '').toLowerCase();
				const right = value.toLowerCase();
				if (op === 'co' || op === 'contains')
					return left.includes(right);
				if (op === 'ne')
					return left !== right;
				return left === right;
			}));
		},

		async _ensureRows() {
			const companyId = this._resolveCompanyIdFromFilter();
			if (!companyId) {
				this._lastRows = [];
				this._lastFilterKey = 'no-company';
				return this._lastRows;
			}
			const status = this._resolveStatusFromFilter();
			const key = `company:${companyId}|status:${status || ''}`;
			if (this._lastRows && this._lastFilterKey === key)
				return this._lastRows;

			const sp = new URLSearchParams();
			if (status) sp.set('status', status);
			sp.set('limit', '500');
			const res = await api.especial_sessp_dose_certa(companyId, sp.toString());
			if (!res.ok)
				throw new Error(formatBiHttpError(res.status, await res.text()));
			const data = await res.json();
			const pacotes = Array.isArray(data?.pacotes) ? data.pacotes : [];
			this._lastRows = pacotes.map((r) => ({
				...r,
				company_id: r.company_id || companyId,
			}));
			this._lastFilterKey = key;
			return this._lastRows;
		},

		async queryData(query) {
			const all = await this._ensureRows();
			const rows = this._applyClientFilter(all);
			const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
			if (qs.get('group') === 'count')
				return rows.length;
			const limit = Number(qs.get('limit') || 30);
			const offset = Number(qs.get('offset') || 0);
			return rows.slice(offset, offset + limit);
		},

		async loadRecord(id_json) {
			const [companyId, inboxId] = JSON.parse(id_json);
			const res = await api.especial_sessp_dose_certa_detail(companyId, inboxId);
			if (!res.ok)
				throw new Error(formatBiHttpError(res.status, await res.text()));
			const data = await res.json();
			const p = data?.pacote || {};
			return {
				id: p.id || inboxId,
				company_id: companyId,
				numero_pedido: p.numero_pedido || '',
				numero_fatura: p.numero_fatura || '',
				status: p.status || '',
				item_count: p.item_count ?? '',
			};
		},

		editRecord(data) {
			Object.getPrototypeOf(this).editRecord.call(this, data);
			setTimeout(() => {
				const root = document.querySelector('#dc-session');
				if (root) root._dcCrud = this;
			}, 0);
		},

		async saveRecord() {
			return false;
		},

		setMenuRoutes() {
			Object.getPrototypeOf(this).setMenuRoutes.call(this);
			router.set('#dose-certa-help', () => openHelp());
			router.set('#dose-certa-open-detail', () => this._openSelectedDetail());
			router.set('#dose-certa-derrubada', () => this._efetuarDerrubada());
			router.set('#dose-certa-enviar-sorter', () => this._enviarSorter());
			router.set('#dose-certa-separacao-padrao', () => this._enviarSeparacaoPadrao());
		},

		_selectionPairs() {
			const sel = this.getSelection() || [];
			return sel
				.map((s) => (Array.isArray(s) ? s : (() => { try { return JSON.parse(s); } catch { return null; } })()))
				.filter((p) => Array.isArray(p) && p.length >= 2);
		},

		_singleCompany(pairs) {
			const companies = new Set(pairs.map((p) => String(p[0])));
			if (companies.size !== 1) {
				notify('Selecione pacotes de uma única empresa.', 'Dose Certa');
				return null;
			}
			return String(pairs[0][0]);
		},

		async _askYes(text, title) {
			return new Promise((resolve) => ask(text, title, (key) => resolve(key === 'Y')));
		},

		async _efetuarDerrubada() {
			const pairs = this._selectionPairs();
			if (!pairs.length) {
				notify('Selecione os pacotes para efetuar a derrubada.', 'Dose Certa');
				return false;
			}
			const companyId = this._singleCompany(pairs);
			if (!companyId) return false;
			const inbox_ids = pairs.map((p) => p[1]);
			if (!await this._askYes(`Gerar a onda de derrubada com ${inbox_ids.length} pacote(s)?`, 'Dose Certa — derrubada'))
				return false;
			try {
				const res = await api.especial_sessp_dose_certa_derrubada(companyId, { inbox_ids });
				if (!res.ok) throw new Error(formatBiHttpError(res.status, await res.text()));
				const data = await res.json();
				const vinc = (data.vinculados || []).length;
				const pulados = (data.pulados || []).length;
				const extra = pulados ? ` ${pulados} pacote(s) ignorado(s) (veja o detalhe).` : '';
				notify(`Onda ${data.wave_id} gerada. ${vinc} pacote(s) com saída reservada.${extra}`, 'Dose Certa — derrubada');
				this.reloadPage();
			} catch (err) {
				notify(err?.message || String(err), 'Dose Certa — derrubada');
			}
			return true;
		},

		async _bulkPerPackage(apiFn, { confirmText, title, successText }) {
			const pairs = this._selectionPairs();
			if (!pairs.length) {
				notify('Selecione ao menos um pacote.', title);
				return false;
			}
			if (!await this._askYes(confirmText.replace('{n}', String(pairs.length)), title))
				return false;
			let ok = 0;
			const erros = [];
			for (const [companyId, inboxId] of pairs) {
				try {
					const res = await apiFn(companyId, inboxId, {});
					if (!res.ok) throw new Error(formatBiHttpError(res.status, await res.text()));
					ok += 1;
				} catch (err) {
					erros.push(`#${inboxId}: ${err?.message || err}`);
				}
			}
			notify(
				erros.length
					? `${successText.replace('{n}', String(ok))}\nFalhas:\n${erros.join('\n')}`
					: successText.replace('{n}', String(ok)),
				title,
			);
			this.reloadPage();
			return true;
		},

		async _enviarSorter() {
			return this._bulkPerPackage(
				(c, i, b) => api.especial_sessp_dose_certa_enviar_sorter(c, i, b),
				{
					confirmText: 'Enviar {n} pacote(s) ao sorter/esteira? (requer derrubada feita)',
					title: 'Dose Certa — sorter',
					successText: '{n} pacote(s) enviado(s) ao sorter.',
				},
			);
		},

		async _enviarSeparacaoPadrao() {
			return this._bulkPerPackage(
				(c, i, b) => api.especial_sessp_dose_certa_separacao_padrao(c, i, b),
				{
					confirmText: 'Enviar {n} pacote(s) para separação padrão (coletor, sem esteira)?',
					title: 'Dose Certa — separação padrão',
					successText: '{n} pacote(s) na fila de separação padrão (coletor).',
				},
			);
		},

		async _openSelectedDetail() {
			const selected = this.getSelection();
			if (selected.length !== 1) {
				notify('Selecione exatamente um pacote para ver o detalhe.');
				return false;
			}
			try {
				const record = await this.loadRecord(JSON.stringify(selected[0]));
				if (record != null)
					this.editRecord(record);
				return true;
			} catch (err) {
				notify(err?.message || String(err), 'Atenção');
				return false;
			}
		},

		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {}) {
			await Object.getPrototypeOf(this).run.call(
				this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports,
				{ ...options, skipInitialLoad: true },
			);
			const insertLi = this.target_node?.querySelector?.('li[hash="#insert"]');
			if (insertLi) insertLi.style.display = 'none';

			// Link da saída: abre a tela Saídas já filtrada (empresa + nº da saída)
			this.target_node.addEventListener('click', (ev) => {
				const a = ev.target?.closest?.('a.dc-open-output');
				if (!a || !this.target_node.contains(a)) return;
				ev.preventDefault();
				ev.stopPropagation();
				const companyId = a.getAttribute('data-company');
				const outputId = a.getAttribute('data-output');
				if (!companyId || !outputId) return;
				openOutputsFiltered({ companyId, outputId });
			}, true);

			// Link da onda: abre a tela Derrubada — ondas já filtrada (empresa + nº da onda)
			this.target_node.addEventListener('click', (ev) => {
				const a = ev.target?.closest?.('a.dc-open-wave');
				if (!a || !this.target_node.contains(a)) return;
				ev.preventDefault();
				ev.stopPropagation();
				const companyId = a.getAttribute('data-company');
				const waveId = a.getAttribute('data-wave');
				if (!companyId || !waveId) return;
				openWavesFiltered({ companyId, waveId });
			}, true);

			let companyId = this._resolveCompanyIdFromFilter();
			if (!companyId) {
				try {
					companyId = localStorage.getItem('especial-dose-certa.company_id');
				} catch (_) { /* ignore */ }
			}
			if (!companyId) {
				try {
					const companies = await db.list('companies');
					const list = Array.isArray(companies) ? companies : [];
					const prefer = list.find((c) => /machado|amil|hospital|sessp|ibl/i.test(c.nick || c.name || ''));
					companyId = (prefer || list[0])?.id || null;
				} catch (_) { /* ignore */ }
			}
			if (companyId)
				this._setCompanyFilter(companyId);

			if (!this._resolveCompanyIdFromFilter()) {
				notify('Use Filtrar e escolha a empresa para carregar a fila Dose Certa.', 'Dose Certa');
				await this.loadPage(1);
				return;
			}
			await this.loadPage(1);
		},
	});

	const fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'programa', label: 'Programa', types: [types.name()], placeholder: 'Ex: 3002' },
		{ index: 3, field: 'status', label: 'Status', types: [types.opt_dose_certa_status()] },
		{ index: 4, field: 'numero_pedido', label: 'Pedido', types: [types.name()], placeholder: '' },
		{ index: 5, field: 'numero_fatura', label: 'Fatura', types: [types.name()], placeholder: '' },
		{ index: 6, field: 'rota', label: 'Rota', types: [types.name()], placeholder: '' },
		{ index: 7, field: 'destinatario', label: 'Destinatário', types: [types.name()], placeholder: '' },
		{ index: 8, field: 'codigo_local', label: 'Local', types: [types.name()], placeholder: 'Ex: SES01' },
		{ index: 9, field: 'output_id', label: 'Saída', types: [types.number()], placeholder: '' },
		{ index: 10, field: 'sorter_status', label: 'Esteira', types: [types.name()], placeholder: 'Pendente, Enviado, Conferido, Coletor…' },
	];

	await dc_crud.run(
		'especial-dose-certa',
		'Dose Certa',
		work,
		view,
		form,
		fieldspecs,
		[
			{ hash: '#dose-certa-open-detail', label: 'Ver detalhe do pacote' },
			{ hash: '#dose-certa-derrubada', label: 'Efetuar&nbsp;derrubada' },
			{ hash: '#dose-certa-enviar-sorter', label: 'Enviar&nbsp;ao&nbsp;sorter' },
			{ hash: '#dose-certa-separacao-padrao', label: 'Separação&nbsp;padrão' },
			{ hash: '#dose-certa-help', label: 'Ajuda da tela' },
		],
		[],
	);
};

export default especialDoseCerta;
